/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([], () => {

	class PxPercentUtil {

		/**
		 *  Convert the model positional values to Percent
		 * @param {Object} positionalModel - An object that contains any of following positional properties
		 * { top: xxx, left: xxx, bottom: xxx, right: xxx, width: xxx, height: xxx }
		 * xxx should be in percent (ex. '25%')
		 * @param {Object} pageSize - An object that describes the page size where the positional model resides
		 * The structure is { width: xxxx, height: yyyy }.
		 * yyy needs to be in pixels (ex. '100px')
		 * @param {Number} decimalPlaces - The number of decimal places to truncate the percent float
		 * @returns Undefined
		 */
		static changePixelPropertiesToPercent(positionalModel, pageSize, decimalPlaces) {
			let converter = new PixelToPercentConverter(decimalPlaces);
			this._convertUnits(positionalModel, pageSize, converter);
		}

		/**
		 *  Convert the model positional values to Pixel
		 * @param {Object} positionalModel - An object that contains any of following positional properties
		 * { top: xxx, left: xxx, bottom: xxx, right: xxx, width: xxx, height: xxx }
		 * xxx should be in pixels (ex. '100px')
		 * @param {Object} pageSize - An object that describes the page size where the positional model resides
		 * The structure is { width: xxxx, height: yyyy }.
		 * yyy needs to be in pixels (ex. '100px')
		 * @returns Undefined
		 */
		static changePercentPropertiesToPixel(positionalModel, pageSize) {
			let converter = new PercentToPixelConverter();
			this._convertUnits(positionalModel, pageSize, converter);
		}

		static _convertUnits(positionalModel, pageSize, converter) {
			if (!positionalModel || !pageSize) {
				throw new Error('Invalid options specified.');
			}

			// Height / top position values
			this._convertProperty(positionalModel, 'height', pageSize.height, converter);
			if (positionalModel['height'] === '100%') {
				positionalModel['top'] = '0%';
			} else {
				this._convertProperty(positionalModel, 'top', pageSize.height, converter);
			}

			// Width / left position values
			this._convertProperty(positionalModel, 'width', pageSize.width, converter);
			if (positionalModel['width'] === '100%') {
				positionalModel['left'] = '0%';
			} else {
				this._convertProperty(positionalModel, 'left', pageSize.width, converter);
			}

			// Some models have right & bottom and others have width & height
			// Bottom and right values are relative to bottom and right side instead of top/left
			this._convertProperty(positionalModel, 'bottom', pageSize.height, converter, false);
			this._convertProperty(positionalModel, 'right', pageSize.width, converter, false);
		}

		static _convertProperty(positionalModel, prop, maxSize, converter, relativeToBottomRight = false) {
			if (positionalModel && positionalModel[prop]) {
				positionalModel[prop] = converter.convertValue(positionalModel[prop], maxSize, relativeToBottomRight);
			}
		}
	}

	class PixelToPercentConverter {
		constructor(decimalPlaces) {
			this.decimalPlaces = decimalPlaces;
		}

		convertValue(value, maxSize, relativeToBottomRight) {
			var result = value;
			if (value && value.indexOf && value.indexOf('px') !== -1) {
				var size = 100 * parseFloat(value) / maxSize;
				if (relativeToBottomRight) {
					size = 100 - size;
				}

				if (this.decimalPlaces) {
					result = Number(size.toFixed(this.decimalPlaces));
				} else {
					result = size;
				}

				if (result > 100) {
					result = 100;
				}

				result += '%';
			}
			return result;
		}
	}

	class PercentToPixelConverter {
		convertValue(value, maxSize, relativeToBottomRight) {
			var result = value;
			if (value && value.indexOf && value.indexOf('%') !== -1) {
				var size = parseFloat(value);
				if (relativeToBottomRight && size === 0) {
					result = maxSize;
				} else {
					result = Math.round(maxSize * size / 100);
				}
				result = result + 'px';
			}
			return result;
		}
	}

	return PxPercentUtil;
});
